import React, { useMemo } from 'react';

import MainButton from '../../../components/agv-button';
import { formatDiffValue } from '../client-diff';
import { isVroomOptimizationEnabled } from '../client-form';
import { FormSection, Panel } from '../clients-ui';
import {
  getFeatureEntry,
  getFeatureLabel,
  sortSectionKeys,
  useFeaturesCatalog,
} from '../feature-catalog';
import { PRICING_FIELDS } from '../steps/pricing-step';

const Row = ({ label, children }) => (
  <div className="tw-flex tw-gap-3 tw-border-0 tw-border-b tw-border-solid tw-border-[#eeeff2] tw-py-1.5 tw-text-[13px] last:tw-border-b-0">
    <span className="tw-w-48 tw-shrink-0 tw-text-[#76808c]">{label}</span>
    <span className="tw-min-w-0 tw-flex-1 tw-break-words tw-text-black">
      {children || '—'}
    </span>
  </div>
);

const EditLink = ({ onClick }) => (
  <MainButton variant="link" text="Modifier" onClick={onClick} />
);

/**
 * Libellé d'une option dans sa section : « Afficher l’heure estimée » pour
 * `TOUR_STEP.DISPLAY_ETA.enabled` (le `enabled` final est la case elle-même).
 */
const getOptionLabel = (catalog, segments) => {
  const optionSegments =
    segments[segments.length - 1] === 'enabled' && segments.length > 1
      ? segments.slice(0, -1)
      : segments;

  return optionSegments
    .slice(1)
    .map((_, index) =>
      getFeatureLabel(catalog, optionSegments.slice(0, index + 2))
    )
    .join(' › ');
};

const isPlainObject = (value) =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

/**
 * Valeurs finales dans l'ordre d'affichage : le `enabled` d'une option avant
 * ses réglages, et rien sous une option décochée (ses réglages sont inertes).
 */
const flattenOptions = (value, path = []) =>
  Object.keys(value)
    .sort((a, b) => (b === 'enabled') - (a === 'enabled'))
    .flatMap((key) => {
      const child = value[key];
      const childPath = [...path, key];

      if (!isPlainObject(child) || Object.keys(child).length === 0)
        return [{ segments: childPath, value: child }];

      if (child.enabled === false)
        return [{ segments: [...childPath, 'enabled'], value: false }];

      return flattenOptions(child, childPath);
    });

/**
 * Options activées et réglages renseignés, par section de premier niveau
 * (dans l'ordre du catalogue).
 */
const useOptionsSummary = (featuresV2) => {
  const catalog = useFeaturesCatalog();

  return useMemo(() => {
    const value = featuresV2 || {};
    const sectionKeys = sortSectionKeys(catalog, Object.keys(value));
    const generalKeys = sectionKeys.filter((key) => !isPlainObject(value[key]));

    const sections = [
      ...(generalKeys.length > 0
        ? [
            {
              key: '__general__',
              title: 'Général',
              options: flattenOptions(
                Object.fromEntries(generalKeys.map((key) => [key, value[key]]))
              ),
            },
          ]
        : []),
      ...sectionKeys
        .filter((key) => isPlainObject(value[key]))
        .map((key) => ({
          key,
          title: getFeatureLabel(catalog, [key]),
          options: flattenOptions({ [key]: value[key] }),
        })),
    ];

    return sections.map(({ key, title, options }) => {
      const section = { key, title, enabled: [], settings: [] };

      options.forEach(({ segments, value: optionValue }) => {
        const path = segments.join('.');
        const optionSegments =
          segments[segments.length - 1] === 'enabled' && segments.length > 1
            ? segments.slice(0, -1)
            : segments;
        const label =
          segments.length > 1
            ? getOptionLabel(catalog, segments)
            : getFeatureLabel(catalog, segments);
        const isUnused = Boolean(
          getFeatureEntry(catalog, optionSegments)?.unused
        );

        if (optionValue === true)
          section.enabled.push({ path, label, isUnused });
        else if (
          optionValue !== false &&
          optionValue !== '' &&
          optionValue != null &&
          !(Array.isArray(optionValue) && optionValue.length === 0)
        )
          section.settings.push({
            path,
            label,
            isUnused,
            value: formatDiffValue(optionValue),
          });
      });

      return section;
    });
  }, [featuresV2, catalog]);
};

/**
 * Récapitulatif complet avant la création d'un client : tout ce qui sera
 * créé, section par section, options comprises.
 */
export default function ClientCreationSummary({
  information,
  featuresV2,
  pricing,
  logoFile,
  adminUsers,
  onEdit,
  steps,
}) {
  const optionsSummary = useOptionsSummary(featuresV2);
  const enabledCount = optionsSummary.reduce(
    (sum, section) => sum + section.enabled.length,
    0
  );

  return (
    <Panel>
      <FormSection
        title="Informations"
        description="Données du client et communications."
        actions={<EditLink onClick={() => onEdit(steps.INFORMATION)} />}
      >
        <div>
          <Row label="Nom du client">{information.name}</Row>
          <Row label="SIRET">{information.siret}</Row>
          <Row label="N° de TVA">{information.tva}</Row>
          <Row label="Adresse">
            {[
              information.street,
              [information.zipcode, information.city].filter(Boolean).join(' '),
              information.country,
            ]
              .filter(Boolean)
              .join(', ')}
          </Row>
          <Row label="Envoi d’emails">
            {information.is_email_sending_enabled ? 'Activé' : 'Désactivé'}
          </Row>
          <Row label="Envoi de SMS">
            {information.is_sms_sending_enabled ? 'Activé' : 'Désactivé'}
          </Row>
          <Row label="Logo">{logoFile ? logoFile.name : 'Aucun logo'}</Row>
        </div>
      </FormSection>

      <FormSection
        title="Options"
        description={`${enabledCount} option${enabledCount > 1 ? 's' : ''} activée${enabledCount > 1 ? 's' : ''}. Les options décochées ne sont pas listées.`}
        actions={<EditLink onClick={() => onEdit(steps.OPTIONS)} />}
      >
        <div className="tw-flex tw-flex-col tw-gap-3">
          {optionsSummary.map((section) => (
            <div key={section.key} className="tw-flex tw-flex-col tw-gap-1">
              <p className="tw-m-0 tw-text-[11px] tw-font-semibold tw-uppercase tw-text-[rgba(0,0,0,0.45)]">
                {section.title}
              </p>
              {section.enabled.length === 0 && section.settings.length === 0 ? (
                <span className="tw-text-[12px] tw-text-[#76808c]">
                  Aucune option activée
                </span>
              ) : (
                <ul className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-0.5 tw-p-0">
                  {section.enabled.map((option) => (
                    <li
                      key={option.path}
                      className="tw-text-[13px] tw-text-black"
                    >
                      <span className="tw-mr-1.5 tw-text-status-positive">
                        ✓
                      </span>
                      {option.label}
                      {option.isUnused && (
                        <span className="tw-ml-1.5 tw-rounded-[3px] tw-bg-amber-50 tw-px-1 tw-text-[11px] tw-font-medium tw-text-amber-800">
                          sans effet
                        </span>
                      )}
                    </li>
                  ))}
                  {section.settings.map((setting) => (
                    <li
                      key={setting.path}
                      className="tw-text-[13px] tw-text-black"
                    >
                      <span className="tw-mr-1.5 tw-text-[#76808c]">•</span>
                      {setting.label} :{' '}
                      <span className="tw-font-mono tw-text-[12px]">
                        {setting.value}
                      </span>
                      {setting.isUnused && (
                        <span className="tw-ml-1.5 tw-rounded-[3px] tw-bg-amber-50 tw-px-1 tw-text-[11px] tw-font-medium tw-text-amber-800">
                          sans effet
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      </FormSection>

      <FormSection
        title="Tarification"
        actions={<EditLink onClick={() => onEdit(steps.PRICING)} />}
      >
        <div>
          {PRICING_FIELDS.map(({ key, label, unit }) => (
            <Row key={key} label={label}>
              {pricing[key] ?? '—'} {unit}
            </Row>
          ))}
        </div>
      </FormSection>

      <FormSection
        title="Administrateurs"
        description="Créés juste après le client."
        actions={<EditLink onClick={() => onEdit(steps.ADMIN)} />}
      >
        {adminUsers.length === 0 ? (
          <span className="tw-text-[13px] tw-text-amber-700">
            Aucun administrateur : personne ne pourra se connecter au
            back-office du client (ajout possible ensuite depuis sa fiche).
          </span>
        ) : (
          <div>
            {adminUsers.map((adminUser) => (
              <Row
                key={adminUser.email}
                label={`${adminUser.first_name} ${adminUser.last_name}`}
              >
                {adminUser.email}
              </Row>
            ))}
            {!information.is_email_sending_enabled && (
              <p className="tw-m-0 tw-pt-2 tw-text-[12px] tw-text-[#76808c]">
                Envoi d’emails désactivé : leurs liens d’inscription
                s’afficheront dans la fiche du client.
              </p>
            )}
          </div>
        )}
      </FormSection>

      <FormSection title="Également créé">
        <Row label="Optimisation VROOM">
          {isVroomOptimizationEnabled(featuresV2)
            ? 'Configuration par défaut (personnalisation à venir)'
            : 'Aucune configuration : l’optimisation VROOM n’est pas activée (créée automatiquement si elle l’est plus tard)'}
        </Row>
      </FormSection>
    </Panel>
  );
}
